/**
 * 配色の定義。ダーク(darkPalette)は従来の tokens.ts の値をそのまま持つ。
 * 値は CSS 変数(--kdk-*)として注入され、コンポーネントは tokens(var() 参照)経由で使う。
 */
export type Palette = {
  bg: string;
  sidebar: string;
  /** サイドバー内の文字・背景・線。ライトではサイドバーが濃い青なので、全体の text.* とは別に持つ。 */
  sidebarTx: string;
  sidebarMu: string;
  sidebarDim: string;
  sidebarHover: string;
  sidebarActive: string;
  sidebarLine: string;
  sidebarLine2: string;
  /** 選択中メニューの左端の線 */
  sidebarAccent: string;
  panel: string;
  panel2: string;
  /** 詳細・編集ドロワーの背景。ドロワー内の固定フッターも同じ色にする。 */
  drawer: string;
  /** 表を月ごとに色分けするときの、1 つおきの月の薄い背景。 */
  monthBand: string;
  line: string;
  line2: string;
  tx: string;
  mu: string;
  dim: string;
  red: string;
  ok: string;
  accent: string;
  blue: string;
  blueHover: string;
  rowHover: string;
  track: string;
  /** 表の見出し行(フェーズ区切り)など、パネルより一段くぼんだ背景 */
  inset: string;
  calendarGrid: string;
  chartGrid: string;
  alertBorder: string;
  /** 配分バーの「100%」を示す縦線 */
  marker: string;
  /** 案件の識別色 hsl(h 55% L) の L。背景に合わせて明るさを変える。 */
  caseL: string;
  /** 表のスクロールバー(SimpleBar)。thumb は track より明るく(濃く)、hover でさらに強くする。 */
  scrollbar: { track: string; thumb: string; thumbHover: string };
  /** 担い手 G(現場) / A(Kセンター) / B(BO) / C(外部) */
  dest: { G: string; A: string; B: string; C: string };
  /** 推進スケジュールの段階 1〜5 */
  stage: { 1: string; 2: string; 3: string; 4: string; 5: string };
  /** 配置カレンダーのセル背景(稼働率の帯)と、超過セルの文字色 */
  load: { lo: string; ok: string; hi: string; over: string; overTx: string };
  /** 未確定案件(引合・受注予定) */
  tentative: { bg: string; border: string; tx: string };
  bar: { need: string; used: string; capacity: string };
  spark: { ok: string; none: string };
};

export const darkPalette: Palette = {
  bg: '#0E1A2B',
  sidebar: '#0A1422',
  sidebarTx: '#E7EEF7',
  sidebarMu: '#93A7C1',
  sidebarDim: '#8399BA',
  sidebarHover: '#15243A',
  sidebarActive: '#1B2E48',
  sidebarLine: '#29405F',
  sidebarLine2: '#36537A',
  sidebarAccent: '#3FC1D9',
  panel: '#15243A',
  panel2: '#1B2E48',
  drawer: '#12213A',
  monthBand: 'rgba(147, 167, 193, 0.07)',
  line: '#29405F',
  line2: '#36537A',
  tx: '#E7EEF7',
  mu: '#93A7C1',
  dim: '#8399BA',
  red: '#F26B7E',
  ok: '#5ED3A2',
  accent: '#3FC1D9',
  blue: '#1F6FD1',
  blueHover: '#2A7FE6',
  rowHover: '#1A2C45',
  track: '#0B1625',
  inset: '#112035',
  calendarGrid: '#1E3049',
  chartGrid: '#22354F',
  alertBorder: '#7A3445',
  marker: '#FFFFFF',
  caseL: '62%',
  scrollbar: { track: '#0B1625', thumb: '#5576A3', thumbHover: '#7C9DC9' },
  dest: { G: '#F2B035', A: '#3FC1D9', B: '#8E9BFF', C: '#5ED3A2' },
  stage: { 1: '#56739B', 2: '#3FA7C9', 3: '#8E9BFF', 4: '#C792EA', 5: '#F2B035' },
  load: { lo: '#16404A', ok: '#1F3550', hi: '#4A3A18', over: '#5A2638', overTx: '#FFC7D0' },
  tentative: { bg: '#3A3420', border: '#8A7330', tx: '#F2D58B' },
  bar: { need: '#3B5F8C', used: '#3FC1D9', capacity: '#F2B035' },
  spark: { ok: '#2F6D7E', none: '#22354F' },
};

/** ライト。主題色は #1E3C82。サイドバーも同じ濃い青にし、文字は白系にする。 */
export const lightPalette: Palette = {
  bg: '#F3F5F9',
  sidebar: '#1E3C82',
  sidebarTx: '#FFFFFF',
  sidebarMu: 'rgba(255, 255, 255, 0.78)',
  sidebarDim: 'rgba(255, 255, 255, 0.62)',
  sidebarHover: 'rgba(255, 255, 255, 0.10)',
  sidebarActive: 'rgba(255, 255, 255, 0.18)',
  sidebarLine: 'rgba(255, 255, 255, 0.18)',
  sidebarLine2: 'rgba(255, 255, 255, 0.45)',
  sidebarAccent: '#9DB8F2',
  panel: '#FFFFFF',
  panel2: '#EEF2F8',
  drawer: '#FFFFFF',
  monthBand: 'rgba(30, 60, 130, 0.05)',
  line: '#D5DCE8',
  line2: '#7C8CA9',
  tx: '#1B2433',
  mu: '#51607A',
  dim: '#5E6D88',
  red: '#C4314B',
  ok: '#12805C',
  accent: '#1E3C82',
  blue: '#1E3C82',
  blueHover: '#2A4FA3',
  rowHover: '#EDF1F8',
  track: '#E6EBF3',
  inset: '#F3F5F9',
  calendarGrid: '#E3E8F0',
  chartGrid: '#E3E8F0',
  alertBorder: '#E5A0AE',
  marker: '#1B2433',
  caseL: '36%',
  scrollbar: { track: '#E6EBF3', thumb: '#72829D', thumbHover: '#5E6F8C' },
  dest: { G: '#8F5A00', A: '#0B7C94', B: '#4F57C9', C: '#12805C' },
  stage: { 1: '#56739B', 2: '#1F7F9E', 3: '#5560C8', 4: '#9B52C4', 5: '#B7791F' },
  load: { lo: '#DDF1F4', ok: '#E8EEF7', hi: '#FBEBC8', over: '#F8D7DE', overTx: '#8A1F36' },
  tentative: { bg: '#FFF6D9', border: '#A67F1F', tx: '#7A5B00' },
  bar: { need: '#5F82B5', used: '#1E3C82', capacity: '#B97A10' },
  spark: { ok: '#4F9AB0', none: '#DCE3EE' },
};

type PaletteNode = string | { readonly [key: string]: PaletteNode };

const flatten = (node: PaletteNode, path: string): Array<readonly [string, string]> =>
  typeof node === 'string'
    ? [[path, node]]
    : Object.entries(node).flatMap(([key, child]) =>
        flatten(child, path === '' ? key : `${path}-${key}`),
      );

/** パレットを [パス, 値] の一覧にする。パスは階層を '-' でつないだもの(dest.G → 'dest-G')。 */
export const flattenPalette = (palette: Palette): ReadonlyArray<readonly [string, string]> =>
  flatten(palette, '');

export const cssVarName = (path: string): string => `--kdk-${path}`;

/** `:root` などに注入する CSS 変数の一覧。 */
export const toCssVars = (palette: Palette): Record<string, string> =>
  Object.fromEntries(flattenPalette(palette).map(([path, value]) => [cssVarName(path), value]));
